import { Button, Switch } from "antd";
import _ from "lodash";
import PresetAlgorithm from "./presetAlgorithm";
import { useEffect, useMemo, useState } from "react";
import usePresetRtcService from "ui/preset/service";
import EditPresetModal from "./editPresetModal";
import { showToast } from "ui/components/common";
import { LoadingOutlined } from "@ant-design/icons";
import PresetList from "./presetList";
import {
  batchGetPresetsInfo,
  batchUpdateEnableInspection,
  getPresetAlgorithm,
  savePresetAlgorithm,
} from "pages/dashboard/virtualGuard/service";
import NoPresetMask from "./noPresetMask";
import PresetStreaming from "./presetStreaming";
import { useCommonService } from "pages/service";
export default function PresetSetting({
  selectedCamera,
  onFailToFetchPresetList,
  selectedSite,
}) {
  const {
    getPresetSnapShotWithDbCache,
    getPresetList,
    deletePreset,
    gotoPreset,
    setPreset,
  } = usePresetRtcService();
  const [presetList, setPresetList] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [presetModalVisible, setPresetModalVisible] = useState(false);
  const [presetLoading, setPresetLoading] = useState(false);
  const [checkedAlgo, setCheckedAlgo] = useState([]);
  const [algoLoading, setAlgoLoading] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [presetLocation, setPresetLocation] = useState(null);
  const [enableInspection, setEnableInspection] = useState(false);
  const [enableAllInspection, setEnableAllInspection] = useState(false);
  const [enableAllLoading, setEnableAllLoading] = useState(false);
  const { takeSnapshot } = useCommonService();
  const selectedPreset = useMemo(() => {
    const match = presetList?.find((preset) => preset.id === selectedPresetId);
    return match;
  }, [presetList, selectedPresetId]);
  function getPresetAlgoSetting() {
    setAlgoLoading(true);
    getPresetAlgorithm(selectedCamera.id, selectedPresetId)
      .then((res) => {
        if (res.status !== 200) {
          showToast(1, "Failed to get preset algorithm");
          return;
        }
        setTagList(res.data?.tags?.map((item) => item.desc) || []);
        setCheckedAlgo(res.data?.algos || []);
        setEnableInspection(Boolean(res.data?.enableInspection));
        if (res.data?.location?.polygon?.latitude) {
          setPresetLocation({
            lat: res.data?.location.polygon.latitude,
            lng: res.data?.location.polygon.longitude,
          });
        } else {
          setPresetLocation(null);
        }
      })
      .finally(() => setAlgoLoading(false));
  }
  useEffect(() => {
    if (!selectedPreset) return;
    getPresetAlgoSetting();
    if (selectedCamera.capabilities?.ptz === false) return;

    setPresetName(selectedPreset.name);
    //retry getting snapshot if snapshot is not exist
    if (!selectedPreset.snapshot) {
      getPresetSnapShotWithDbCache(
        +selectedCamera.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        selectedCamera,
        selectedPresetId,
        selectedPreset.modifiedAt,
      ).then(async (blob) => {
        selectedPreset.snapshot = URL.createObjectURL(blob);
        setPresetList((state) => {
          const index = state.findIndex((item) => item.id === selectedPresetId);
          state[index] = selectedPreset;
          return [...state];
        });
      });
    }
  }, [selectedPreset]);
  async function refreshPresetList() {
    if (selectedCamera?.capabilities?.ptz === false) {
      setPresetList([
        {
          id: 1,
          modifiedAt: 0,
          name: "Static Preset",
          algorithm: [],
          snapshot: null,
        },
      ]);
      return takeSnapshot(
        [selectedCamera],
        selectedCamera.orgId,
        (snapShots) => {
          setPresetList([
            {
              id: 1,
              modifiedAt: 0,
              name: "Static Preset",
              algorithm: [],
              snapshot: URL.createObjectURL(snapShots[0].blob),
            },
          ]);
        },
      );
    }
    setPresetLoading(true);
    let _presetList = [];
    return getPresetList(
      +selectedCamera?.orgId,
      +selectedCamera.nvrId,
      selectedCamera?.devId,
      selectedCamera,
    )
      .then((res) => {
        console.log("presetList res", res);
        _presetList = res.presetList.map((item) => ({
          id: item.id,
          modifiedAt: item.modifiedAt,
          name: item.name,
          algorithm: [],
          snapshot: null,
        }));
        setPresetList([..._presetList]);
        setSelectedPresetId(null);
        _presetList.forEach((preset) => {
          getPresetSnapShotWithDbCache(
            +selectedCamera.orgId,
            +selectedCamera.nvrId,
            selectedCamera?.devId,
            selectedCamera,
            preset.id,
            preset.modifiedAt.toString(),
          ).then(async (blob) => {
            preset.snapshot = URL.createObjectURL(blob);
            setPresetList([..._presetList]);
          });
        });
        setPresetLoading(false);
        return batchGetPresetsInfo(selectedCamera.id);
      })
      .then((res) => {
        if (res.status === 200) {
          // get allEnableInspection status
          const presetList = res.data.data;
          let allInspectionEnabled = true;
          // only if preset exist in edge is valid
          _presetList.forEach((preset) => {
            preset.enableInspection = Boolean(
              presetList.find((item) => item.index === preset.id)
                ?.enableInspection,
            );
            if (!preset.enableInspection) {
              allInspectionEnabled = false;
            }
          });
          setEnableAllInspection(allInspectionEnabled);
        } else {
          showToast(1, "Failed to fetch presets enable Inspection status");
        }
      })
      .catch((err) => {
        console.error(err);
        setPresetLoading(false);
        setPresetList([]);
        setSelectedPresetId(null);
        onFailToFetchPresetList();
        showToast(1, "Failed to fetch preset list");
      });
  }
  useEffect(() => {
    if (selectedCamera) {
      setPresetList([]);
      setSelectedPresetId(null);
      refreshPresetList();
    }
  }, [selectedCamera]);
  async function onPresetUpdated(id) {
    await refreshPresetList();
    setPresetModalVisible(false);
    setSelectedPresetId(id);
  }
  function onCreatePreset() {
    setPresetModalVisible(true);
  }
  async function onSaveAlgo() {
    if (presetName.length > 30) {
      showToast(1, "Preset name should not exceed 30 characters");
      return;
    }
    if (checkedAlgo.length > 10) {
      showToast(1, "Selected algorithm should not exceed 10");
      return;
    }
    if (selectedCamera.capabilities?.ptz === true) {
      await setPreset(
        +selectedCamera?.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        selectedCamera,
        selectedPresetId,
        presetName,
      );
      onPresetUpdated(selectedPresetId);
    }
    savePresetAlgorithm(
      selectedCamera.id,
      selectedPresetId,
      checkedAlgo,
      tagList,
      presetLocation,
      enableInspection,
    )
      .then((res) => {
        if (res.status !== 200) {
          showToast(1, "Failed to save preset algorithm");
        } else {
          showToast(0, "Preset Algorithm saved successfully");
        }
      })
      .finally(getPresetAlgoSetting);
  }

  async function onPresetDelete() {
    try {
      await deletePreset(
        +selectedCamera.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        selectedCamera,
        selectedPresetId,
      );
      const algoRes = await savePresetAlgorithm(
        selectedCamera.id,
        selectedPresetId,
        [],
        [],
        null,
        false,
      );
      if (algoRes.status == 200) {
        await refreshPresetList();
        setSelectedPresetId(null);
        showToast(0, "Preset Deleted successfully");
      } else {
        showToast(1, "Failed to delete preset");
      }
    } catch (e) {
      showToast(1, "Failed to delete preset");
      throw e;
    }
  }

  function onCheckAlgo(target, checked) {
    setCheckedAlgo((state) => {
      const match = state.find((item) => item.name === target.name);
      if (checked && !match) {
        return [...state, target];
      }
      if (!checked && match) {
        return state.filter((item) => item.name !== target.name);
      }
      return state;
    });
  }
  function onSelectPreset(id) {
    setSelectedPresetId(id);
    if (selectedCamera.capabilities?.ptz === true) {
      gotoPreset(
        +selectedCamera.orgId,
        +selectedCamera.nvrId,
        selectedCamera?.devId,
        selectedCamera,
        id,
      );
    }
  }
  function onSelectAlgoTemplate(algos) {
    setCheckedAlgo(_.cloneDeep(algos || []));
  }
  async function onAllEnableInspectionChange(checked) {
    setEnableAllLoading(true);
    const res = await batchUpdateEnableInspection(
      selectedCamera.id,
      presetList.map((preset) => preset.id),
      checked,
    );
    setEnableAllLoading(false);
    if (res.status !== 200) {
      showToast(1, "Failed to update enable inspection status");
    } else {
      await refreshPresetList();
      setEnableAllInspection(checked);
    }
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "1.25rem",
        height: "100%",
      }}
    >
      {presetLoading && (
        <div className="mask" style={{ zIndex: 2, textAlign: "center" }}>
          <LoadingOutlined />
        </div>
      )}
      <div style={{ flex: "none", maxHeight: "460px" }}>
        <div>
          <span>Preset Setting</span>
          {selectedCamera.capabilities?.ptz && (
            <span style={{ float: "right" }}>
              <Switch
                checked={enableAllInspection}
                loading={enableAllLoading}
                onChange={onAllEnableInspectionChange}
                style={{ marginRight: "8px" }}
              />
              Enable All Inspectors
              <Button
                type="link"
                disabled={presetList.length >= 16}
                onClick={onCreatePreset}
              >
                + Preset
              </Button>
            </span>
          )}
        </div>
        <PresetList
          presetList={presetList}
          selectedPresetId={selectedPresetId}
          onChangeSelectedPresetId={onSelectPreset}
        />
      </div>
      <div
        style={{
          width: "100%",
          borderTop: "1px solid rgba(225, 225, 225, 1)",
          margin: "16px 0px",
        }}
      ></div>
      <div
        style={{
          flex: "1",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          gap: "10px",
          overflow: "auto",
          justifyContent: "space-between",
        }}
      >
        <NoPresetMask presetList={presetList} onAddPreset={onCreatePreset} />
        {presetList.length && !selectedPreset && (
          <>
            <div
              className="mask"
              style={{
                zIndex: 1,
                textAlign: "center",
                backgroundColor: "white",
              }}
            >
              <div
                style={{ color: "black", fontWeight: "600", fontSize: "20px" }}
              >
                Select a Preset to Configure
              </div>
              <div style={{ color: "black" }}>
                Choose a preset from the list above to edit its configuration.
              </div>
            </div>
          </>
        )}
        {algoLoading && (
          <div
            className="mask"
            style={{
              zIndex: 1,
              textAlign: "center",
              borderRadius: "8px",
              background: "transparent",
            }}
          >
            <LoadingOutlined style={{ color: "black" }} />
          </div>
        )}
        <div
          style={{
            textAlign: "center",
            flex: "none",
            width: "100%",
            height: "237px",
          }}
        >
          <PresetStreaming
            camera={selectedCamera}
            onTagListChange={setTagList}
            tagList={tagList}
            presetName={presetName}
            onPresetNameChange={(e) => setPresetName(e.target.value)}
            presetLocation={presetLocation}
            onPresetLocationChange={setPresetLocation}
            selectedSite={selectedSite}
          />
        </div>
        <div style={{ flex: "1" }}>
          <PresetAlgorithm
            loading={algoLoading}
            checkedAlgo={checkedAlgo}
            onCheckAlgo={onCheckAlgo}
            onSelectAlgoTemplate={onSelectAlgoTemplate}
          />
        </div>
        {presetList?.length && selectedPreset && (
          <div
            style={{
              flex: "none",
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span
              style={{
                alignContent: "center",
                fontWeight: "600",
                fontSize: "16px",
              }}
            >
              <Switch
                checked={enableInspection}
                onChange={setEnableInspection}
                style={{ marginRight: "8px" }}
              />
              Enable Inspector
            </span>

            <span>
              <Button className="primaryButton danger" onClick={onPresetDelete}>
                Delete Preset
              </Button>
              <Button className="primaryButton" onClick={onSaveAlgo}>
                Save
              </Button>
            </span>
          </div>
        )}
      </div>

      <EditPresetModal
        visible={presetModalVisible}
        camera={selectedCamera}
        onClose={() => setPresetModalVisible(false)}
        onPresetUpdated={onPresetUpdated}
        presetList={presetList}
      />
    </div>
  );
}
